import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { debounceTime, Subscription } from 'rxjs';
import { LocalStorageService } from '../../local-storage/local-storage.service';
import { CuestionarioSaludService } from '../../services/cuestionario-salud/cuestionario-salud.service';
import { CuestionarioSalud } from '../../models/cuestionario-salud/cuestionario-salud';
import { FiltroCuestionarioSalud } from '../../models/filtro-cuestionario-salud/filtro-cuestionario-salud';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RespuestaSalud } from '../../models/interfaz-auditoria/interfaz-auditoria-request';

@Component({
  selector: 'app-cuestionario-salud-card',
  templateUrl: './cuestionario-salud-card.component.html',
  styleUrls: ['./cuestionario-salud-card.component.css'],
})
export class CuestionarioSaludCardComponent {
  form!: FormGroup;
  cuestionarios: CuestionarioSalud[] = [];
  @Input() nombrePersona: string = '';
  @Input() storageKey!: string;
  @Input() usuario!: FiltroCuestionarioSalud;
  @Input() readonly = false;
  private formSub?: Subscription;
  @Output() errorCuestionario = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
    private cuestionarioService: CuestionarioSaludService,
    private modalService: NgbModal,
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.cuestionarioService
      .getCuestionarioSaludConFiltros(this.usuario)
      .subscribe({
        next: (response) => {
          this.cuestionarios = response;
          this.crearControles();
          this.restoreFromLocalStorage();

          if (this.readonly) {
            this.form.disable({ emitEvent: false });
          } else {
            this.listenForChanges();
          }

        },
        error: (err) => {
          console.log(err);
          if (err.status === 404) {
            this.errorCuestionario.emit(err);
          }
        },
      });
  }

  isValid(): boolean {
    return this.form.valid;
  }

  getValues(): RespuestaSalud[] {
    return this.getRespuestasFormateadas();
  }

  private restoreFromLocalStorage() {
    const state = this.localStorageService.load(this.storageKey);

    if (state) {
      this.form.patchValue(JSON.parse(state));
    }
  }

  private listenForChanges() {
    this.formSub = this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => {
        this.localStorageService.save(this.storageKey, JSON.stringify(value));
      });
  }

  private crearControles(): void {
    this.cuestionarios.forEach((cuestionario) => {
      cuestionario.secciones.forEach((seccion) => {
        seccion.preguntas.forEach((pregunta) => {
          if (pregunta.tipoPregunta.tipoPregunta === 'LISTA') {
            const grupoOpciones = this.fb.group(
              {},
              pregunta.obligatoria ? { validators: this.listaValidator() } : [],
            );

            pregunta.opciones.forEach((opcion: any) => {
              grupoOpciones.addControl(
                'opcion_' + opcion.id,
                this.fb.control(false),
              );
            });

            this.form.addControl('pregunta_' + pregunta.id, grupoOpciones);
            return;
          }

          const controlName =
            pregunta.tipoPregunta.tipoPregunta === 'FECHA'
              ? 'fecha_' + pregunta.id
              : 'pregunta_' + pregunta.id;

          // const validators = [];
          const validators = pregunta.preguntaPadre
            ? []
            : pregunta.obligatoria
              ? [Validators.required]
              : [];

          if (pregunta.obligatoria) {
            validators.push(Validators.required);
          }

          this.form.addControl(controlName, this.fb.control(null, validators));
        });
      });
    });

    // ─── Escuchar cambios en padres para actualizar validación de hijas ───
    this.cuestionarios.forEach((cuestionario) => {
      cuestionario.secciones.forEach((seccion) => {
        seccion.preguntas.forEach((pregunta) => {
          if (pregunta.preguntaPadre) {
            this.escucharPadre(pregunta);
          }
        });
      });
    });
  }

  private escucharPadre(preguntaHija: any): void {
    const controlPadre = this.form.get(
      'pregunta_' + preguntaHija.preguntaPadre,
    );
    const controlHija = this.form.get('pregunta_' + preguntaHija.id);

    if (!controlPadre || !controlHija) return;

    controlPadre.valueChanges.subscribe((valor) => {
      if (valor === 'SI') {
        // Padre seleccionó SI → hija es obligatoria
        if (preguntaHija.obligatoria) {
          controlHija.setValidators([Validators.required]);
        }
      } else {
        // Padre seleccionó NO o null → hija no es obligatoria y se limpia
        controlHija.clearValidators();
        controlHija.setValue(null, { emitEvent: false });
      }
      controlHija.updateValueAndValidity();
    });
  }

  listaValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const algunaSeleccionada = Object.values(group.value).some(
        (valor) => valor === true,
      );

      return algunaSeleccionada ? null : { requeridoLista: true };
    };
  }

  onCheckboxChange(event: any, pregunta: any, opcionSeleccionadaId: number) {
    if (pregunta.seleccionMultiple) return;

    if (!event.checked) return;

    console.log(pregunta);

    const grupoPregunta = this.form.get('pregunta_' + pregunta.id);

    if (!grupoPregunta) return;

    pregunta.opciones.forEach((opcion: any) => {
      if (opcion.id !== opcionSeleccionadaId) {
        grupoPregunta
          .get('opcion_' + opcion.id)
          ?.setValue(false, { emitEvent: false });
      }
    });
  }

  debeMostrarPregunta(pregunta: any): boolean {
    if (!pregunta.preguntaPadre) {
      return true;
    }

    const controlPadre = this.form.get('pregunta_' + pregunta.preguntaPadre);

    if (!controlPadre) return false;

    const valor = controlPadre.value;

    return valor === 'SI';
  }

  getRespuestasFormateadas(): RespuestaSalud[] {
    const resultado: RespuestaSalud[] = [];
    const formValue = this.form.value;

    this.cuestionarios.forEach((cuestionario) => {
      cuestionario.secciones.forEach((seccion) => {
        seccion.preguntas.forEach((pregunta) => {
          if (pregunta.preguntaPadre) {
            const valorPadre = formValue['pregunta_' + pregunta.preguntaPadre];
            if (valorPadre !== 'SI') return;
          }

          const tipoPregunta = pregunta.tipoPregunta.tipoPregunta;
          let respuesta: any;

          if (tipoPregunta === 'FECHA') {
            respuesta = formValue['fecha_' + pregunta.id];
          } else if (tipoPregunta === 'LISTA') {
            const grupoOpciones = formValue['pregunta_' + pregunta.id];
            respuesta = Object.entries(grupoOpciones)
              .filter(([_, valor]) => valor === true)
              .map(([key]) => key.replace('opcion_', ''));
          } else {
            respuesta = formValue['pregunta_' + pregunta.id];
          }

          resultado.push({
            identificacion: this.usuario.identificacion ?? null,
            cuestionarioId: cuestionario.id,
            seccionId: seccion.id,
            preguntaId: pregunta.id,
            tipoPregunta: tipoPregunta,
            respuesta: respuesta,
          });
        });
      });
    });

    return resultado;
  }

  getSnapshot(): any[] {
    const formValue = this.form.value;

    return this.cuestionarios.map((cuestionario) => {
      const preguntas: any[] = [];

      cuestionario.secciones.forEach((seccion) => {
        seccion.preguntas.forEach((pregunta) => {
          if (pregunta.preguntaPadre) return;
          preguntas.push(this.buildPreguntaSnapshot(pregunta, formValue));
        });
      });

      return {
        idCopiaCuestionario: cuestionario.id,
        idLineaNegocio: this.usuario.lineaNegocioId ?? null,
        idGrupoPrograma: this.usuario.grupoProgramaId ?? null,
        edad: this.usuario.edad ?? null,
        genero: this.usuario.generoId ?? null,
        idIntegrante: this.usuario.identificacion ?? null, // 👈 ya está en usuario
        jsonDataSnapShot: { preguntas },
      };
    });
  }

  private buildPreguntaSnapshot(pregunta: any, formValue: any): any {
    const tipoPregunta = pregunta.tipoPregunta.tipoPregunta;
    let respuesta: any;

    if (tipoPregunta === 'FECHA') {
      respuesta = formValue['fecha_' + pregunta.id];
    } else if (tipoPregunta === 'LISTA') {
      const grupoOpciones = formValue['pregunta_' + pregunta.id];
      // Unir las opciones seleccionadas con ";"
      respuesta = Object.entries(grupoOpciones)
        .filter(([_, valor]) => valor === true)
        .map(([key]) => {
          // Buscar el texto de la opción por su id
          const opcionId = parseInt(key.replace('opcion_', ''), 10);
          const opcion = pregunta.opciones.find((o: any) => o.id === opcionId);
          return opcion ? opcion.texto : opcionId;
        })
        .join('; ');
    } else {
      respuesta = formValue['pregunta_' + pregunta.id];
    }

    // Construir hijas solo si padre respondió "si"
    const hijas: any[] = [];
    if (respuesta === 'SI') {
      this.cuestionarios.forEach((c) => {
        c.secciones.forEach((s) => {
          s.preguntas.forEach((p) => {
            if (p.preguntaPadre === pregunta.id) {
              hijas.push(this.buildPreguntaSnapshot(p, formValue));
            }
          });
        });
      });
    }

    return {
      id: pregunta.id,
      orden: pregunta.orden,
      texto: pregunta.enunciando,
      tipo: tipoPregunta,
      respuesta: respuesta ?? null,
      hijas,
    };
  }
}
